import { useState, useEffect, useCallback, useRef } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  CheckCircle,
  Circle,
  ExternalLink,
  Database,
  Globe,
  Users,
  Key,
  Compass,
  CreditCard,
  Settings,
  ArrowRight,
  UserPlus,
  Rocket,
  FileText,
  Loader2,
  Copy,
  Check,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import {
  onboardingApi,
  type OnboardingState,
  type OnboardingStepId,
  type OnboardingDecisions,
  type SolutionFocusValue,
  type ImplementationType,
} from "@/services/api/onboarding";

export const Route = createFileRoute("/portal/platform/client-onboarding")({
  component: ClientOnboardingPage,
});

const ALL_STEP_IDS: OnboardingStepId[] = [
  "create_org",
  "nda",
  "data_hosting",
  "database_requirements",
  "fixed_ip",
  "collaboration",
  "solution_focus",
  "credentials",
  "identify_owner",
  "trial_setup",
  "payment",
  "configure_connectors",
  "poc",
  "go_live",
];

const LARASOFT_HOSTED_SKIPPED: OnboardingStepId[] = [
  "database_requirements",
  "fixed_ip",
];

interface StepDef {
  id: OnboardingStepId;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  audience: "internal" | "client";
  conditional?: string;
}

const STEP_DEFS: StepDef[] = [
  {
    id: "create_org",
    title: "Create the Organisation",
    icon: Settings,
    audience: "internal",
    description:
      "[Larasoft Portal] Add the prospect as an organisation before the demo meeting",
  },
  {
    id: "nda",
    title: "Non-Disclosure Agreement",
    icon: FileText,
    audience: "client",
    description:
      '[Client Decision] "Do you have your own NDA, or would you like to review ours?"',
  },
  {
    id: "data_hosting",
    title: "Data Hosting Decision",
    icon: Database,
    audience: "client",
    description:
      '[Client Decision] "Will you host your own database, or would you like us to host it for you?"',
  },
  {
    id: "database_requirements",
    title: "Database Requirements",
    icon: Database,
    audience: "client",
    conditional: "If client-hosted",
    description:
      "[Client Action] Azure SQL S1 minimum, dedicated credentials, full table permissions",
  },
  {
    id: "fixed_ip",
    title: "Fixed IP Access",
    icon: Globe,
    audience: "client",
    conditional: "If client-hosted",
    description:
      '[Client Decision] "Does your database server require fixed IP access?"',
  },
  {
    id: "collaboration",
    title: "Collaboration Channel",
    icon: Users,
    audience: "client",
    description:
      '[Client Decision] "How would you like us to share project documents?"',
  },
  {
    id: "solution_focus",
    title: "Solution Focus",
    icon: Compass,
    audience: "client",
    description:
      '[Client Decision] "What are you primarily looking to achieve?"',
  },
  {
    id: "credentials",
    title: "Secure Credential Sharing",
    icon: Key,
    audience: "client",
    description:
      "[Client Action] Share database credentials and API keys securely",
  },
  {
    id: "identify_owner",
    title: "Identify the Organisation Owner",
    icon: UserPlus,
    audience: "client",
    description:
      "[Client Decision] Confirm who should be the owner on the client's side",
  },
  {
    id: "trial_setup",
    title: "Set Up Trial Subscription",
    icon: CreditCard,
    audience: "internal",
    description:
      "[Larasoft Portal] Apply 100% discount for 30-day trial period",
  },
  {
    id: "payment",
    title: "Add Payment Details",
    icon: CreditCard,
    audience: "client",
    description:
      "[Client Action] Add credit or debit card details via Paystack in the portal",
  },
  {
    id: "configure_connectors",
    title: "Configure Connectors and Data Sources",
    icon: Settings,
    audience: "internal",
    description:
      "[Larasoft Portal] Set up connectors and data sources using their credentials",
  },
  {
    id: "poc",
    title: "Implementation",
    icon: Rocket,
    audience: "internal",
    description:
      "[Larasoft Portal] Based on the solution focus, build and deliver the solution",
  },
  {
    id: "go_live",
    title: "Go Live",
    icon: Rocket,
    audience: "internal",
    description:
      "[Internal] Review results, transition to paid subscription, hand over to account management",
  },
];

const SOLUTION_FOCUS_OPTIONS: {
  value: SolutionFocusValue;
  label: string;
  description: string;
}[] = [
  {
    value: "connectors",
    label: "Connectors",
    description: "Data integration from external systems",
  },
  {
    value: "reporting",
    label: "Reporting",
    description: "Dashboards and data visualisation",
  },
  {
    value: "myapp",
    label: "MyApp",
    description: "Custom application workflows",
  },
  {
    value: "workflows",
    label: "Workflows",
    description: "Automated business process workflows",
  },
  {
    value: "kb_file",
    label: "Knowledgebase (File)",
    description: "File-based AI knowledge base",
  },
  {
    value: "kb_database",
    label: "Knowledgebase (DB)",
    description: "Database-connected AI knowledge base",
  },
  {
    value: "marketing",
    label: "Marketing",
    description: "Email campaigns and automation",
  },
  {
    value: "agentic_ai",
    label: "Agentic AI",
    description: "AI-driven automation",
  },
];

const IMPLEMENTATION_OPTIONS: {
  value: ImplementationType;
  label: string;
  description: string;
}[] = [
  {
    value: "pilot",
    label: "Pilot",
    description: "Time-boxed proof of concept before full rollout",
  },
  {
    value: "implementation",
    label: "Straight Implementation",
    description: "Direct build and go-live",
  },
  {
    value: "poc",
    label: "PoC",
    description: "Proof of concept to validate the approach",
  },
  {
    value: "other",
    label: "Other",
    description: "Custom engagement type",
  },
];

/**
 * Login/user provisioning the client's DBA runs on their own Azure SQL server
 * so Larasoft can read, write and manage tables. Rendered on the "Database
 * Requirements" step, which is disabled (and so never rendered) once hosting is
 * decided as Larasoft-hosted.
 *
 * Deliberately TWO blocks, not one. Azure SQL requires CREATE LOGIN to be the
 * only statement in its batch and to run against `master`, and has no USE — so
 * the two halves need different connections and cannot be one paste.
 * https://learn.microsoft.com/en-us/sql/t-sql/statements/create-login-transact-sql?view=azuresqldb-current
 *
 * The password placeholder is intentionally NOT a valid string literal: an
 * unedited paste must fail with a syntax error rather than succeed and create a
 * real login whose password is published in this file.
 */
const DB_ADMIN_SQL_MASTER = `-- Connect to the Azure SQL SERVER, database: master
-- REPLACE the placeholder below -- angle brackets and all -- with a generated
-- 24+ character password IN SINGLE QUOTES, e.g. 'Xk4x...'. Without the quotes
-- you will get "Incorrect syntax near" pointing at your own password.

CREATE LOGIN larasoftserviceadmin
WITH PASSWORD = <REPLACE_WITH_GENERATED_24_CHAR_PASSWORD>;
GO`;

const DB_ADMIN_SQL_DATABASE = `-- Now change the SSMS connection to the CLIENT DATABASE
-- (Azure SQL has no USE statement - you must reconnect.)

CREATE USER larasoftserviceadmin
FOR LOGIN larasoftserviceadmin
WITH DEFAULT_SCHEMA = dbo;
GO

ALTER ROLE db_datareader ADD MEMBER larasoftserviceadmin;
ALTER ROLE db_datawriter ADD MEMBER larasoftserviceadmin;
ALTER ROLE db_ddladmin ADD MEMBER larasoftserviceadmin;
GO`;

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function CopyButton({
  value,
  title,
  successMessage,
  className = "ml-2",
}: {
  value: string;
  title: string;
  successMessage: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The timer is armed inside .then(), i.e. possibly AFTER unmount — the
  // accordion is single-open, so collapsing this step while the clipboard write
  // is still pending unmounts us. Clearing timerRef alone would miss that case
  // (there is nothing to clear yet), so gate the resolve path on mounted too.
  const mountedRef = useRef(true);

  useEffect(
    () => () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  return (
    <button
      type="button"
      onClick={() => {
        // Only report success once the write actually resolves — a rejected
        // write (permission denied, unfocused tab) or a missing clipboard API
        // (non-secure origin) must not leave the user pasting stale content
        // while the button says "Copied".
        (navigator.clipboard
          ? navigator.clipboard.writeText(value)
          : Promise.reject(new Error("clipboard unavailable")))
          .then(() => {
            toast.success(successMessage);
            if (!mountedRef.current) return;
            setCopied(true);
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => setCopied(false), 2000);
          })
          .catch(() =>
            toast.error("Copy failed — select the text and copy manually"),
          );
      }}
      className={`inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 ${className}`}
      title={title}
    >
      {copied ? (
        <Check className="h-3 w-3" />
      ) : (
        <Copy className="h-3 w-3" />
      )}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function SqlBlock({
  step,
  sql,
  copyTitle,
  copyMessage,
}: {
  step: string;
  sql: string;
  copyTitle: string;
  copyMessage: string;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold">{step}</p>
        <CopyButton
          value={sql}
          title={copyTitle}
          successMessage={copyMessage}
          className="shrink-0"
        />
      </div>
      <pre className="overflow-x-auto rounded-lg border bg-slate-50 p-3 text-xs font-mono leading-relaxed dark:bg-slate-900">
        <code>{sql}</code>
      </pre>
    </div>
  );
}

function ClientOnboardingPage() {
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const orgId = selectedOrganisation?.organisationId;

  const [state, setState] = useState<OnboardingState | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState<Set<string>>(new Set());
  const activeOrgRef = useRef(orgId);

  const isLarasoftHosted = state?.decisions?.hosting === "larasoft";

  const fetchState = useCallback(async () => {
    if (!token || !orgId) return;
    activeOrgRef.current = orgId;
    try {
      setLoading(true);
      setLoadError(false);
      const res = await onboardingApi.getOnboarding(token, orgId);
      if (activeOrgRef.current !== orgId) return;
      setState(
        res.onboarding_state ?? {
          version: 1,
          steps: {},
          decisions: {},
        },
      );
    } catch (err) {
      if (activeOrgRef.current !== orgId) return;
      toast.error("Failed to load onboarding state");
      setLoadError(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token, orgId]);

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  const patchStep = useCallback(
    async (stepId: OnboardingStepId, done: boolean) => {
      if (!token || !orgId || !state) return;

      const snapshot = structuredClone(state);
      const requestOrgId = orgId;
      setSaving((prev) => new Set(prev).add(stepId));

      setState((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          steps: {
            ...prev.steps,
            [stepId]: { ...prev.steps[stepId], done },
          },
        };
      });

      try {
        const res = await onboardingApi.updateOnboarding(token, requestOrgId, {
          steps: { [stepId]: { done } },
        });
        if (activeOrgRef.current === requestOrgId && res.onboarding_state) {
          setState(res.onboarding_state);
        }
      } catch (err) {
        toast.error("Failed to save step");
        console.error(err);
        if (activeOrgRef.current === requestOrgId) {
          setState(snapshot);
        }
      } finally {
        setSaving((prev) => {
          const next = new Set(prev);
          next.delete(stepId);
          return next;
        });
      }
    },
    [token, orgId, state],
  );

  const patchDecision = useCallback(
    async (decisions: Partial<OnboardingDecisions>) => {
      if (!token || !orgId || !state) return;

      const snapshot = structuredClone(state);
      const requestOrgId = orgId;

      setState((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          decisions: { ...prev.decisions, ...decisions },
        };
      });

      try {
        const res = await onboardingApi.updateOnboarding(token, requestOrgId, {
          decisions,
        });
        if (activeOrgRef.current === requestOrgId && res.onboarding_state) {
          setState(res.onboarding_state);
        }
      } catch (err) {
        toast.error("Failed to save decision");
        console.error(err);
        if (activeOrgRef.current === requestOrgId) {
          setState(snapshot);
        }
      }
    },
    [token, orgId, state],
  );

  const isStepDone = (id: OnboardingStepId): boolean =>
    state?.steps[id]?.done ?? false;

  const isStepDisabled = (id: OnboardingStepId): boolean =>
    isLarasoftHosted === true &&
    LARASOFT_HOSTED_SKIPPED.includes(id);

  const completedCount = ALL_STEP_IDS.filter(
    (id) => isStepDone(id) || isStepDisabled(id),
  ).length;
  const totalSteps = ALL_STEP_IDS.length;

  if (!orgId) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <p className="text-muted-foreground">
          Select an organisation to view its onboarding progress.
        </p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="mx-auto max-w-3xl p-6 space-y-3">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
          <p className="font-medium">Failed to load onboarding state</p>
          <p className="mt-1 text-xs">
            The control plane may be unavailable. Check connectivity and try
            again.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchState}>
          Retry
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl p-6 flex items-center justify-center gap-2">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        <span className="text-muted-foreground">
          Loading onboarding state&hellip;
        </span>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          Client Onboarding
        </h1>
        <p className="text-muted-foreground">
          From demo conversation through proof of concept to live &mdash; work
          through each step to get{" "}
          <strong>{selectedOrganisation?.organisationName}</strong> running.
        </p>
      </div>

      {/* Audience legend */}
      <div className="flex gap-4 text-xs font-medium">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-amber-500" />
          Internal (our team)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-sky-500" />
          Client action
        </span>
      </div>

      {/* Progress */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Onboarding Progress</CardTitle>
          <CardDescription>
            {completedCount === totalSteps
              ? "All steps completed! The client is live."
              : `${completedCount} of ${totalSteps} steps completed`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-1.5">
            {ALL_STEP_IDS.map((id) => (
              <div
                key={id}
                className={`h-2 flex-1 rounded-full transition-colors ${
                  isStepDone(id)
                    ? "bg-green-500"
                    : isStepDisabled(id)
                      ? "bg-green-300 dark:bg-green-800"
                      : "bg-slate-200 dark:bg-slate-700"
                }`}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Steps */}
      <div className="rounded-2xl border bg-card p-4 shadow-sm sm:p-6">
        <Accordion type="single" collapsible className="space-y-2">
          {STEP_DEFS.map((step, index) => {
            const done = isStepDone(step.id);
            const disabled = isStepDisabled(step.id);
            const isSaving = saving.has(step.id);
            const StepIcon = step.icon;
            const audienceColor =
              step.audience === "internal"
                ? "border-l-amber-500"
                : "border-l-sky-500";
            const stepState = state?.steps[step.id];

            return (
              <AccordionItem
                key={step.id}
                value={step.id}
                className={`border rounded-lg border-l-[3px] px-4 ${audienceColor} ${
                  disabled ? "opacity-40" : ""
                }`}
                disabled={disabled}
              >
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      disabled={isSaving || disabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!disabled) patchStep(step.id, !done);
                      }}
                      className="shrink-0"
                    >
                      {isSaving ? (
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      ) : done ? (
                        <CheckCircle className="h-6 w-6 text-green-500" />
                      ) : (
                        <Circle className="h-6 w-6 text-slate-300 dark:text-slate-600" />
                      )}
                    </button>
                    <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                      <StepIcon className="h-4 w-4 text-primary" />
                    </div>
                    <div className="text-left">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">
                          Step {index + 1}
                        </span>
                        {step.conditional && (
                          <Badge
                            variant="outline"
                            className="text-xs font-normal"
                          >
                            {step.conditional}
                          </Badge>
                        )}
                        {disabled && (
                          <Badge
                            variant="outline"
                            className="text-xs font-normal"
                          >
                            Not required
                          </Badge>
                        )}
                        {done && (
                          <Badge variant="success" className="text-xs">
                            Done
                          </Badge>
                        )}
                      </div>
                      <div className="font-medium">{step.title}</div>
                      {done && stepState?.at && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Completed {formatTimestamp(stepState.at)}
                          {stepState.by && ` by ${stepState.by}`}
                        </p>
                      )}
                    </div>
                  </div>
                </AccordionTrigger>
                {!disabled && (
                  <AccordionContent className="pl-[4.5rem] pb-4">
                    <p className="text-sm text-muted-foreground mb-3">
                      {step.description}
                    </p>
                    <StepDetails
                      stepId={step.id}
                      decisions={state?.decisions ?? {}}
                      onDecisionChange={patchDecision}
                    />
                  </AccordionContent>
                )}
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>

      {/* Completion */}
      {completedCount === totalSteps && (
        <Card className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <CheckCircle className="h-8 w-8 shrink-0 text-green-500" />
              <div>
                <h3 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-1">
                  Client is live!
                </h3>
                <p className="text-sm text-green-800 dark:text-green-200 mb-3">
                  All onboarding steps are complete.{" "}
                  <strong>{selectedOrganisation?.organisationName}</strong> is
                  set up and running.
                </p>
                <Link
                  to="/portal/platform/organisations"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" size="sm">
                    View Organisations <ArrowRight className="ml-1 h-3 w-3" />
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step detail panels with decision capture controls
// ---------------------------------------------------------------------------

interface StepDetailsProps {
  stepId: OnboardingStepId;
  decisions: Partial<OnboardingDecisions>;
  onDecisionChange: (d: Partial<OnboardingDecisions>) => void;
}

function DebouncedInput({
  value,
  placeholder,
  onCommit,
}: {
  value: string;
  placeholder: string;
  onCommit: (val: string) => void;
}) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  return (
    <input
      type="text"
      placeholder={placeholder}
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => {
        if (local !== value) onCommit(local);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.currentTarget.blur();
        }
      }}
      className="w-full rounded-md border px-3 py-2 text-sm bg-background"
    />
  );
}

function DecisionCard({
  selected,
  onClick,
  title,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg border p-3 text-left transition-colors ${
        selected
          ? "border-primary bg-primary/5 ring-1 ring-primary"
          : "hover:border-primary/50"
      }`}
    >
      <p className="text-sm font-semibold">{title}</p>
      <div className="text-xs text-muted-foreground">{children}</div>
    </button>
  );
}

function StepDetails({
  stepId,
  decisions,
  onDecisionChange,
}: StepDetailsProps) {
  switch (stepId) {
    case "create_org":
      return (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Create the organisation in the Larasoft portal so you can capture
            decisions in real time during the meeting.
          </p>
          <Link
            to="/portal/platform/organisations"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="outline" size="sm">
              Go to Organisations <ExternalLink className="ml-1 h-3 w-3" />
            </Button>
          </Link>
        </div>
      );

    case "nda":
      return (
        <div className="space-y-3">
          <p className="text-sm font-medium">
            &ldquo;Do you have your own NDA, or would you like to review
            ours?&rdquo;
          </p>
          <div className="grid gap-2 sm:grid-cols-3">
            <DecisionCard
              selected={decisions.nda === "ours"}
              onClick={() => onDecisionChange({ nda: "ours" })}
              title="Ours"
            >
              Provide link to Larasoft NDA for review and signature
            </DecisionCard>
            <DecisionCard
              selected={decisions.nda === "theirs"}
              onClick={() => onDecisionChange({ nda: "theirs" })}
              title="Theirs"
            >
              Receive, review internally, sign and return
            </DecisionCard>
            <DecisionCard
              selected={decisions.nda === "not_required"}
              onClick={() => onDecisionChange({ nda: "not_required" })}
              title="Not Required"
            >
              No NDA needed for this engagement
            </DecisionCard>
          </div>
          {decisions.nda === "ours" && (
            <a
              href="/legal/Larasoft_NDA_Latest.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="outline" size="sm">
                View Larasoft NDA <ExternalLink className="ml-1 h-3 w-3" />
              </Button>
            </a>
          )}
        </div>
      );

    case "data_hosting":
      return (
        <div className="space-y-3">
          <p className="text-sm font-medium">
            &ldquo;Will you host your own database in Microsoft Azure, or would
            you like us to host it for you?&rdquo;
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            <DecisionCard
              selected={decisions.hosting === "client"}
              onClick={() => onDecisionChange({ hosting: "client" })}
              title="Client-hosted"
            >
              Client provisions Azure SQL &mdash; proceed to database
              requirements and fixed IP steps
            </DecisionCard>
            <DecisionCard
              selected={decisions.hosting === "larasoft"}
              onClick={() => onDecisionChange({ hosting: "larasoft" })}
              title="Larasoft-hosted"
            >
              We provision the database &mdash; steps 4 and 5 are not required
            </DecisionCard>
          </div>
        </div>
      );

    case "database_requirements":
      return (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            The client needs to provision a Microsoft Azure SQL database with
            these minimum requirements:
          </p>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li>
              Minimum <strong>Azure SQL S1 tier</strong>
            </li>
            <li>
              Dedicated SQL username and password (
              <strong>minimum 24-character password</strong>)
            </li>
            <li>Credentials must not be shared with anyone else</li>
            <li>Permissions: create, edit, and delete tables and records</li>
          </ul>
          <div className="space-y-3">
            <div>
              <p className="text-sm font-medium">
                Script for the client&apos;s database administrator
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Two blocks, run in SSMS in order &mdash; Azure SQL needs a
                different connection for each. Replace the placeholder password
                with a generated one (24+ characters) and share it back through
                step 8 &mdash; secure credential sharing.
              </p>
            </div>
            <SqlBlock
              step="1. Connected to the server, database master"
              sql={DB_ADMIN_SQL_MASTER}
              copyTitle="Copy master SQL"
              copyMessage="master script copied"
            />
            <SqlBlock
              step="2. Reconnected to the client database"
              sql={DB_ADMIN_SQL_DATABASE}
              copyTitle="Copy database SQL"
              copyMessage="Database script copied"
            />
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 dark:bg-amber-950 dark:border-amber-800 dark:text-amber-200">
              These are database-wide roles: the account can read, write and
              change the schema of <strong>every</strong> table in that database,
              not just the ones we create.
            </div>
          </div>
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800 dark:bg-blue-950 dark:border-blue-800 dark:text-blue-200">
            <strong>Other cloud providers</strong> (AWS RDS, etc.) are possible
            but Azure SQL is preferred.
          </div>
        </div>
      );

    case "fixed_ip":
      return (
        <div className="space-y-3">
          <p className="text-sm font-medium">
            &ldquo;Does your database server require fixed IP access?&rdquo;
          </p>
          <div className="flex items-center gap-3">
            <Switch
              checked={decisions.fixedIp ?? false}
              onCheckedChange={(checked) =>
                onDecisionChange({ fixedIp: checked })
              }
            />
            <span className="text-sm">
              {decisions.fixedIp
                ? "Yes — client must allowlist our IP"
                : "No restriction required"}
            </span>
          </div>
          <div className="rounded-lg border p-3 text-sm flex items-center">
            Our fixed IP:{" "}
            <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-mono dark:bg-slate-800 ml-1">
              20.87.213.32
            </code>
            <CopyButton
              value="20.87.213.32"
              title="Copy IP address"
              successMessage="IP copied"
            />
          </div>
        </div>
      );

    case "collaboration":
      return (
        <div className="space-y-3">
          <p className="text-sm font-medium">
            &ldquo;How would you like us to share project documents and
            artefacts?&rdquo;
          </p>
          <div className="grid gap-2 sm:grid-cols-3">
            <DecisionCard
              selected={decisions.collaborationChannel === "teams"}
              onClick={() =>
                onDecisionChange({ collaborationChannel: "teams" })
              }
              title="Microsoft Teams"
            >
              Client creates a Team and invites{" "}
              <span className="font-mono text-xs">steve@larasoft.global</span>{" "}
              and{" "}
              <span className="font-mono text-xs">allan@larasoft.global</span>
            </DecisionCard>
            <DecisionCard
              selected={decisions.collaborationChannel === "sharepoint"}
              onClick={() =>
                onDecisionChange({ collaborationChannel: "sharepoint" })
              }
              title="SharePoint"
            >
              We create a shared folder with client access
            </DecisionCard>
            <DecisionCard
              selected={decisions.collaborationChannel === "gdrive"}
              onClick={() =>
                onDecisionChange({ collaborationChannel: "gdrive" })
              }
              title="Google Shared Drive"
            >
              Client creates and shares with us (for Google shops)
            </DecisionCard>
          </div>
        </div>
      );

    case "solution_focus":
      return (
        <div className="space-y-3">
          <p className="text-sm font-medium">
            &ldquo;What are you primarily looking to achieve?&rdquo;
          </p>
          <p className="text-xs text-muted-foreground">
            Select all that apply &mdash; this informs which modules to activate
            and what the implementation will cover.
          </p>
          <div className="grid gap-2 sm:grid-cols-3">
            {SOLUTION_FOCUS_OPTIONS.map((opt) => {
              const selected =
                decisions.solutionFocus?.includes(opt.value) ?? false;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    const current = decisions.solutionFocus ?? [];
                    const next = selected
                      ? current.filter((v) => v !== opt.value)
                      : [...current, opt.value];
                    onDecisionChange({ solutionFocus: next });
                  }}
                  className={`rounded-lg border p-3 text-left transition-colors ${
                    selected
                      ? "border-primary bg-primary/5 ring-1 ring-primary"
                      : "hover:border-primary/50"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={selected}
                      className="pointer-events-none"
                    />
                    <span className="text-sm font-semibold">{opt.label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {opt.description}
                  </p>
                </button>
              );
            })}
            {/* Other — free text */}
            <button
              type="button"
              onClick={() => {
                const current = decisions.solutionFocus ?? [];
                const hasOther = current.includes("other");
                const next = hasOther
                  ? current.filter((v) => v !== "other")
                  : [...current, "other" as SolutionFocusValue];
                onDecisionChange({
                  solutionFocus: next,
                  ...(!hasOther ? {} : { solutionFocusOther: null }),
                });
              }}
              className={`rounded-lg border p-3 text-left transition-colors ${
                decisions.solutionFocus?.includes("other")
                  ? "border-primary bg-primary/5 ring-1 ring-primary"
                  : "hover:border-primary/50"
              }`}
            >
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={decisions.solutionFocus?.includes("other") ?? false}
                  className="pointer-events-none"
                />
                <span className="text-sm font-semibold">Other</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Something not listed above
              </p>
            </button>
          </div>
          {decisions.solutionFocus?.includes("other") && (
            <DebouncedInput
              value={decisions.solutionFocusOther ?? ""}
              placeholder="Describe the focus area..."
              onCommit={(val) =>
                onDecisionChange({ solutionFocusOther: val })
              }
            />
          )}
        </div>
      );

    case "credentials":
      return (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Database credentials and API keys must be shared one secret per
            message using a secure method:
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="rounded-lg border p-3">
              <p className="text-sm font-semibold">One Time Secret</p>
              <p className="text-xs text-muted-foreground">
                Free &mdash; link expires after first view.{" "}
                <a
                  href="https://onetimesecret.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary underline"
                >
                  onetimesecret.com <ExternalLink className="h-3 w-3" />
                </a>
              </p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-sm font-semibold">Password manager</p>
              <p className="text-xs text-muted-foreground">
                Share directly from 1Password, Bitwarden, or similar
              </p>
            </div>
          </div>
          <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800 dark:bg-red-950 dark:border-red-800 dark:text-red-200">
            <strong>Never accept credentials via plain email.</strong> One secret
            per message &mdash; never combine username and password in a single
            share.
          </div>
        </div>
      );

    case "identify_owner":
      return (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Since the organisation was created in step 1, confirm who should be
            added as the <strong>owner</strong> on the client&apos;s side. We add
            this person manually.
          </p>
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800 dark:bg-blue-950 dark:border-blue-800 dark:text-blue-200">
            The <strong>owner</strong> manages their team from there &mdash;
            inviting additional users and assigning roles. User rights and
            permissions are explained at the invite stage within the portal.
          </div>
        </div>
      );

    case "trial_setup":
      return (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Apply a 100% discount for a 30-day trial period. After the trial,
            billing begins.
          </p>
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800 dark:bg-blue-950 dark:border-blue-800 dark:text-blue-200">
            <strong>Pricing:</strong> From <strong>R10,000/month</strong>, scaled
            to what the client needs &mdash; more connectors, more AI actions, or
            both.
          </div>
          <Link
            to="/portal/platform/subscriptions"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="outline" size="sm">
              Go to Subscriptions <ExternalLink className="ml-1 h-3 w-3" />
            </Button>
          </Link>
        </div>
      );

    case "payment":
      return (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            The client adds credit or debit card details in the portal. Payments
            are processed via <strong>Paystack</strong>, a Stripe company.
          </p>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-300">
            <a
              href="https://paystack.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary underline"
            >
              paystack.com <ExternalLink className="h-3 w-3" />
            </a>{" "}
            &mdash;{" "}
            <a
              href="https://stripe.com/newsroom/news/paystack-joining-stripe"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary underline"
            >
              Stripe on Paystack <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      );

    case "configure_connectors":
      return (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Using the credentials shared earlier, set up the client&apos;s
            connectors and data sources in the portal.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            <Link
              to="/portal/connectors"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
              >
                <ExternalLink className="mr-1 h-3 w-3" /> Client connectors
              </Button>
            </Link>
            <Link
              to="/portal/apps/reporting"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
              >
                <ExternalLink className="mr-1 h-3 w-3" /> Reporting data
                sources
              </Button>
            </Link>
            <Link
              to="/portal/myapp"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
              >
                <ExternalLink className="mr-1 h-3 w-3" /> MyApp data sources
              </Button>
            </Link>
            <Link
              to="/portal/ai-content"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
              >
                <ExternalLink className="mr-1 h-3 w-3" /> KB Database sources
              </Button>
            </Link>
          </div>
        </div>
      );

    case "poc":
      return (
        <div className="space-y-3">
          <p className="text-sm font-medium">
            What type of engagement is this?
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {IMPLEMENTATION_OPTIONS.map((opt) => (
              <DecisionCard
                key={opt.value}
                selected={decisions.implementationType === opt.value}
                onClick={() =>
                  onDecisionChange({
                    implementationType: opt.value,
                    ...(opt.value !== "other"
                      ? { implementationTypeOther: null }
                      : {}),
                  })
                }
                title={opt.label}
              >
                {opt.description}
              </DecisionCard>
            ))}
          </div>
          {decisions.implementationType === "other" && (
            <DebouncedInput
              value={decisions.implementationTypeOther ?? ""}
              placeholder="Describe the engagement type..."
              onCommit={(val) =>
                onDecisionChange({ implementationTypeOther: val })
              }
            />
          )}
          <p className="text-sm text-muted-foreground">
            Based on the solution focus, build and deliver the solution:
          </p>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li>
              <strong>Reporting</strong> &mdash; dashboards and data
              visualisations
            </li>
            <li>
              <strong>MyApp</strong> &mdash; workflows with real data
            </li>
            <li>
              <strong>Knowledgebase</strong> &mdash; connected database or
              uploaded files, test queries
            </li>
            <li>
              <strong>Connectors</strong> &mdash; configured and tested
              integrations
            </li>
            <li>
              <strong>Marketing</strong> &mdash; campaigns and automation flows
            </li>
            <li>
              <strong>Workflows</strong> &mdash; automated business processes
            </li>
          </ul>
        </div>
      );

    case "go_live":
      return (
        <div className="space-y-3">
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li>Review results with the client</li>
            <li>Transition from trial to paid subscription</li>
            <li>Confirm ongoing support channel</li>
            <li>Hand over to account management</li>
          </ul>
        </div>
      );
  }
}
